import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  PiggyBank, CalendarDays, TimerReset, AlertTriangle, Wallet,
  RefreshCw, Loader2, Building2,
} from "lucide-react";
import TrendChart from "@/components/FinanceTrendChart";

const ENT_TREND_SERIES = [
  { key: "total", label: "Total", color: "#059669", width: 3 },
  { key: "annual", label: "Annual leave", color: "#0284c7" },
  { key: "personal", label: "Personal", color: "#f59e0b" },
  { key: "lsl", label: "LSL", color: "#8b5cf6" },
  { key: "redundancy", label: "Redundancy", color: "#dc2626" },
];

const fmtMoney = (v) => new Intl.NumberFormat("en-AU", {
  style: "currency", currency: "AUD", maximumFractionDigits: 0,
}).format(v || 0);
const fmtMoney2 = (v) => new Intl.NumberFormat("en-AU", {
  style: "currency", currency: "AUD", maximumFractionDigits: 2,
}).format(v || 0);
const fmtHrs = (v) => (v == null ? "—" : `${Number(v).toFixed(1)}h`);
const fmtNumber = (v) => new Intl.NumberFormat("en-AU").format(v || 0);
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.toLocaleDateString("en-AU")} ${d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}`;
};

const BUCKETS = [
  {
    key: "annual",
    label: "Annual Leave",
    icon: CalendarDays,
    description: "Accrued annual-leave balance × ordinary rate + 17.5% loading.",
  },
  {
    key: "personal",
    label: "Personal / Sick Leave",
    icon: TimerReset,
    description: "Accrued personal-leave balance × rate. Not paid on termination but still a balance-sheet liability.",
  },
  {
    key: "lsl",
    label: "Long Service Leave (LSL)",
    icon: PiggyBank,
    description: "State-based LSL accrual × rate. Casuals in eligible states are included.",
  },
  {
    key: "redundancy",
    label: "Redundancy on-cost",
    icon: AlertTriangle,
    description: "NES redundancy weeks by tenure × 38h × ordinary rate. Casuals: $0.",
  },
  {
    key: "notice",
    label: "Termination notice",
    icon: AlertTriangle,
    description: "NES minimum notice weeks by tenure (+1 week if 45+ and 2+ years) × 38h × rate.",
  },
];

export default function EntitlementHoldingsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedClient, setExpandedClient] = useState(null);

  const load = useCallback(async (force) => {
    setLoading(true);
    try {
      const { data } = force
        ? await api.post("/finance-holdings/entitlements/refresh")
        : await api.get("/finance-holdings/entitlements");
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load entitlement holdings");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(false); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/finance-holdings/entitlements/refresh");
      setData(data);
      toast.success("Entitlement holdings refreshed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    }
    setRefreshing(false);
  };

  const totals = data?.totals || {};
  const clients = data?.clients || [];
  const total = totals.total || 0;

  const bucketTotal = (key) => totals[key] || 0;

  const allEmployees = useMemo(() => {
    return clients.flatMap((c) => (c.employees || []).map((e) => ({ ...e, _client: c.client_id })));
  }, [clients]);

  return (
    <div className="p-6 max-w-7xl space-y-6" data-testid="entitlement-holdings-page">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-emerald-100 text-emerald-700">
          <PiggyBank className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-[280px]">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Finance &amp; Payroll</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Entitlement Holdings</h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-3xl">
            Cash we should have in reserve if every staff member drew down their statutory
            leave entitlements at once. Aggregated live from KeyPay leave balances and priced
            at current ordinary-time earnings.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data && (
            <span className="text-[11px] text-zinc-500">
              {data.from_cache ? "Cached" : "Fresh"} · {fmtDateTime(data.created_at)}
            </span>
          )}
          <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing}
            data-testid="entitlement-holdings-refresh-btn">
            {refreshing
              ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              : <RefreshCw className="h-4 w-4 mr-1" />}
            Refresh from KeyPay
          </Button>
        </div>
      </div>

      {/* Total holdings summary card */}
      <Card className="border-emerald-200 bg-emerald-50/30">
        <CardContent className="p-6 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Total entitlements owed</div>
            <div className="text-4xl font-extrabold text-emerald-800 mt-1"
              data-testid="entitlement-holdings-total">
              {loading && !data ? "…" : fmtMoney(total)}
            </div>
            <div className="text-xs text-zinc-500 mt-1">
              Sum of all leave and termination liabilities across {clients.length} KeyPay client{clients.length === 1 ? "" : "s"} · {data?.employee_count || 0} employees.
            </div>
          </div>
          <Wallet className="h-14 w-14 text-emerald-300" />
        </CardContent>
      </Card>

      {/* Per-bucket breakdown */}
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {BUCKETS.map((b) => (
          <Card key={b.key} className="border-zinc-200"
            data-testid={`entitlement-bucket-${b.key}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <b.icon className="h-4 w-4 text-emerald-600" />
                  <div className="font-semibold text-sm">{b.label}</div>
                </div>
                <div className="text-lg font-bold text-zinc-800">
                  {loading && !data ? "…" : fmtMoney(bucketTotal(b.key))}
                </div>
              </div>
              <p className="text-xs text-zinc-500 leading-relaxed">{b.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <TrendChart kind="entitlements" days={90} series={ENT_TREND_SERIES} />

      {/* Per-client breakdown */}
      {clients.length > 0 && (
        <Card className="border-zinc-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold text-sm flex items-center gap-1.5">
                <Building2 className="h-4 w-4 text-zinc-500" />
                Per-client breakdown
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead className="text-right">Employees</TableHead>
                  <TableHead className="text-right">Annual</TableHead>
                  <TableHead className="text-right">Personal</TableHead>
                  <TableHead className="text-right">LSL</TableHead>
                  <TableHead className="text-right">Redundancy</TableHead>
                  <TableHead className="text-right">Notice</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.map((c) => (
                  <React.Fragment key={c.client_id}>
                    <TableRow className="cursor-pointer hover:bg-zinc-50"
                      onClick={() => setExpandedClient(expandedClient === c.client_id ? null : c.client_id)}
                      data-testid={`entitlement-client-row-${c.client_id}`}>
                      <TableCell className="font-medium">
                        {c.client_id} <span className="text-xs text-zinc-500">· business {c.business_id}</span>
                        {c.error && (
                          <div className="text-xs text-red-600 mt-1">Error: {c.error}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{fmtNumber(c.employee_count || 0)}</TableCell>
                      <TableCell className="text-right">{fmtMoney(c.totals?.annual)}</TableCell>
                      <TableCell className="text-right">{fmtMoney(c.totals?.personal)}</TableCell>
                      <TableCell className="text-right">{fmtMoney(c.totals?.lsl)}</TableCell>
                      <TableCell className="text-right">{fmtMoney(c.totals?.redundancy)}</TableCell>
                      <TableCell className="text-right">{fmtMoney(c.totals?.notice)}</TableCell>
                      <TableCell className="text-right font-semibold text-emerald-700">
                        {fmtMoney(c.totals?.total)}
                      </TableCell>
                    </TableRow>
                    {expandedClient === c.client_id && (c.employees || []).map((e) => (
                      <TableRow key={`${c.client_id}-${e.employee_id}`} className="bg-zinc-50/50">
                        <TableCell className="pl-8 text-sm">
                          {e.employee_name}
                          {e.is_casual && (
                            <Badge variant="outline"
                              className="ml-2 bg-zinc-100 text-zinc-600 border-zinc-200 text-[9px] px-1 py-0">
                              casual
                            </Badge>
                          )}
                          <div className="text-[11px] text-zinc-500 mt-0.5">
                            ${e.hourly_rate}/h · {Number(e.years_service).toFixed(1)} yrs · A {fmtHrs(e.leave_hours?.annual)} · P {fmtHrs(e.leave_hours?.personal)} · LSL {fmtHrs(e.leave_hours?.lsl)}
                          </div>
                        </TableCell>
                        <TableCell />
                        <TableCell className="text-right text-sm text-zinc-600">{fmtMoney2(e.values?.annual)}</TableCell>
                        <TableCell className="text-right text-sm text-zinc-600">{fmtMoney2(e.values?.personal)}</TableCell>
                        <TableCell className="text-right text-sm text-zinc-600">{fmtMoney2(e.values?.lsl)}</TableCell>
                        <TableCell className="text-right text-sm text-zinc-600">{fmtMoney2(e.values?.redundancy)}</TableCell>
                        <TableCell className="text-right text-sm text-zinc-600">{fmtMoney2(e.values?.notice)}</TableCell>
                        <TableCell className="text-right text-sm font-semibold text-zinc-800">
                          {fmtMoney2(e.values?.total)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
            {clients.length > 0 && (
              <div className="text-[11px] text-zinc-500 mt-2 italic">
                Click a client row to expand employee-level detail.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {clients.length === 0 && !loading && (
        <Card className="border-dashed border-zinc-300">
          <CardContent className="p-10 text-center text-zinc-500 text-sm">
            No KeyPay clients configured — connect one under <b>KeyPay (Employment Hero)</b> to see live entitlement liabilities here.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
